import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

function SideBar() {
  const [isCoursesOpen, setIsCoursesOpen] = useState(false);
  const [isBatchOpen, setIsBatchOpen] = useState(false);
  const [isStudentsOpen, setIsStudentsOpen] = useState(false);
  const [isBlogsOpen, setIsBlogsOpen] = useState(false);
  const [isWebsiteOpen, setIsWebsiteOpen] = useState(false);
  const [isHomepageOpen, setIsHomepageOpen] = useState(false);
  const [isAttendanceOpen, setIsAttendanceOpen] = useState(false);
  const [isMockExamOpen, setIsMockExamOpen] = useState(false);
  const navigate = useNavigate();
  const [unreadRegistrations, setUnreadRegistrations] = useState(0);

  // Unread website registrations, shown as a badge on the menu item.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const token = JSON.parse(sessionStorage.getItem('adminSession'))?.token;
        if (!token) return;
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/registrations/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.data.success) setUnreadRegistrations(res.data.unread || 0);
      } catch (err) {
        /* the badge is optional */
      }
    };
    load();
    const timer = setInterval(load, 60000);
    window.addEventListener('registrations-changed', load);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('registrations-changed', load);
    };
  }, []);

  const toggleCoursesDropdown = () => setIsCoursesOpen(!isCoursesOpen);
  const toggleStudentsDropdown = () => setIsStudentsOpen(!isStudentsOpen);
  const toggleBlogsDropdown = () => setIsBlogsOpen(!isBlogsOpen);
  const toggleWebsiteDropdown = () => setIsWebsiteOpen(!isWebsiteOpen);
  const toggleHomepageDropdown = () => setIsHomepageOpen(!isHomepageOpen);
  const toggleAttendanceDropdown = () => setIsAttendanceOpen(!isAttendanceOpen);
  const toggleMockExamDropdown = () => setIsMockExamOpen(!isMockExamOpen);

  const handleLogout = () => {
    sessionStorage.removeItem('adminSession');
    navigate('/admin-login');
  };

  useEffect(() => {
    console.log('SideBar mounted. Initial dropdown states:', {
      isCoursesOpen,
      isStudentsOpen,
      isBlogsOpen,
      isWebsiteOpen
    });

    // Debug submenu visibility and class application
    const submenus = document.querySelectorAll('.sidebar-submenu');
    submenus.forEach((submenu, index) => {
      const isOpen = submenu.classList.contains('open');
      console.log(`Submenu ${index + 1} display:`, window.getComputedStyle(submenu).display, `open class: ${isOpen}`);
    });

    // Debug icon rendering
    const icons = document.querySelectorAll('.dropdown-icon');
    icons.forEach((icon, index) => {
      console.log(`Icon ${index + 1} class:`, icon.className);
    });

    // Ensure dropdown buttons are clickable
    const dropdownButtons = document.querySelectorAll('.sidebar-menu__link.has-dropdown');
    dropdownButtons.forEach((btn, index) => {
      btn.addEventListener('click', () => {
        console.log(`Dropdown button clicked: ${btn.textContent}`);
      });
    });

    // Cleanup event listeners
    return () => {
      dropdownButtons.forEach((btn) => {
        btn.removeEventListener('click', () => {});
      });
    };
  }, [isCoursesOpen, isStudentsOpen, isBlogsOpen, isWebsiteOpen, isHomepageOpen]);

  return (
    <>
      {/*==================== Sidebar Overlay End ====================*/}
      <div className="side-overlay" />
      {/*==================== Sidebar Overlay End ====================*/}
      {/* ============================ Sidebar Start ============================ */}
      <aside className="sidebar">
        {/* sidebar close btn */}
        <button
          type="button"
          className="sidebar-close-btn text-gray-500 hover-text-white hover-bg-main-600 text-md w-24 h-24 border border-gray-100 hover-border-main-600 d-xl-none d-flex flex-center rounded-circle position-absolute"
        >
          <i className="ph ph-x" />
        </button>
        {/* sidebar close btn */}
        <Link
          to="/admin-dashboard"
          className="sidebar__logo text-center p-20 position-sticky inset-block-start-0 bg-white w-100 z-1 pb-10"
        >
          <img src="/assets/images/logo/logo.jpg" alt="Logo" />
        </Link>
        <div className="sidebar-menu-wrapper overflow-y-auto scroll-sm">
          <div className="p-20 pt-10">
            <ul className="sidebar-menu">
              <li className="sidebar-menu__item">
                <Link to="/admin-dashboard" className="sidebar-menu__link">
                  <span className="icon">
                    <i className="ph ph-squares-four" />
                  </span>
                  <span className="text">Dashboard</span>
                </Link>
              </li>

              <li className="sidebar-menu__item">
                <Link to="/admin-registrations" className="sidebar-menu__link">
                  <span className="icon">
                    <i className="ph ph-user-plus" />
                  </span>
                  <span className="text">Registrations</span>
                  {unreadRegistrations > 0 && (
                    <span
                      className="badge bg-danger text-white rounded-pill ms-auto px-8 py-2 text-12"
                      title={`${unreadRegistrations} unread`}
                    >
                      {unreadRegistrations > 99 ? '99+' : unreadRegistrations}
                    </span>
                  )}
                </Link>
              </li>

              <li className="sidebar-menu__item">
                <Link to="/admin-document-views" className="sidebar-menu__link">
                  <span className="icon">
                    <i className="ph ph-eye" />
                  </span>
                  <span className="text">Document Views</span>
                </Link>
              </li>

              <li className={`sidebar-menu__item ${isCoursesOpen ? 'active' : ''}`}>
                <button
                  type="button"
                  className="sidebar-menu__link"
                  onClick={toggleCoursesDropdown}
                  aria-expanded={isCoursesOpen}
                >
                  <span className="icon">
                    <i className="ph ph-graduation-cap" />
                  </span>
                  <span className="text">Courses</span>
                  <i className={`ph ph-caret-down dropdown-icon ${isCoursesOpen ? 'rotate-180' : ''}`} />
                </button>
                {/* Submenu start */}
                <ul
                  className={`sidebar-submenu ${isCoursesOpen ? 'open' : ''}`}
                  style={{ display: isCoursesOpen ? 'block' : 'none' }}
                >
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-add-course" className="sidebar-submenu__link">
                      Add Courses
                    </Link>
                  </li>
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-view-course" className="sidebar-submenu__link">
                      View Courses
                    </Link>
                  </li>
                   <li className="sidebar-submenu__item">
                    <Link to="/admin-view-batches" className="sidebar-submenu__link">
                      Batch Manager
                    </Link>
                  </li>
                </ul>
                {/* Submenu End */}
              </li>



              <li className={`sidebar-menu__item ${isStudentsOpen ? 'active' : ''}`}>
                <button
                  type="button"
                  className="sidebar-menu__link"
                  onClick={toggleStudentsDropdown}
                  aria-expanded={isStudentsOpen}
                >
                  <span className="icon">
                    <i className="ph ph-users-three" />
                  </span>
                  <span className="text">Students</span>
                  <i className={`ph ph-caret-down dropdown-icon ${isStudentsOpen ? 'rotate-180' : ''}`} />
                </button>
                {/* Submenu start */}
                <ul
                  className={`sidebar-submenu ${isStudentsOpen ? 'open' : ''}`}
                  style={{ display: isStudentsOpen ? 'block' : 'none' }}
                >
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-add-student" className="sidebar-submenu__link">
                      Add Students
                    </Link>
                  </li>
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-view-students" className="sidebar-submenu__link">
                      View Students
                    </Link>
                  </li>
                   <li className="sidebar-submenu__item">
                    <Link to="/admin-student-allocation" className="sidebar-submenu__link">
                      Students and Courses
                    </Link>
                  </li>
                </ul>
                {/* Submenu End */}
              </li>

              <li className={`sidebar-menu__item ${isAttendanceOpen ? 'active' : ''}`}>
                <button
                  type="button"
                  className="sidebar-menu__link"
                  onClick={toggleAttendanceDropdown}
                  aria-expanded={isAttendanceOpen}
                >
                  <span className="icon">
                    <i className="ph ph-qr-code" />
                  </span>
                  <span className="text">Attendance</span>
                  <i className={`ph ph-caret-down dropdown-icon ${isAttendanceOpen ? 'rotate-180' : ''}`} />
                </button>
                {/* Submenu start */}
                <ul
                  className={`sidebar-submenu ${isAttendanceOpen ? 'open' : ''}`}
                  style={{ display: isAttendanceOpen ? 'block' : 'none' }}
                >
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-attendance-qr" className="sidebar-submenu__link">
                      Generate QR Code
                    </Link>
                  </li>
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-attendance-report" className="sidebar-submenu__link">
                      Attendance Report
                    </Link>
                  </li>
                </ul>
                {/* Submenu End */}
              </li>

              <li className={`sidebar-menu__item ${isMockExamOpen ? 'active' : ''}`}>
                <button
                  type="button"
                  className="sidebar-menu__link"
                  onClick={toggleMockExamDropdown}
                  aria-expanded={isMockExamOpen}
                >
                  <span className="icon">
                    <i className="ph ph-clipboard-text" />
                  </span>
                  <span className="text">Mock Exams</span>
                  <i className={`ph ph-caret-down dropdown-icon ${isMockExamOpen ? 'rotate-180' : ''}`} />
                </button>
                {/* Submenu start */}
                <ul
                  className={`sidebar-submenu ${isMockExamOpen ? 'open' : ''}`}
                  style={{ display: isMockExamOpen ? 'block' : 'none' }}
                >
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-mock-exam-banks" className="sidebar-submenu__link">
                      Question Banks
                    </Link>
                  </li>
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-mock-exams" className="sidebar-submenu__link">
                      Exams
                    </Link>
                  </li>
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-mock-exam-reports" className="sidebar-submenu__link">
                      Reports
                    </Link>
                  </li>
                </ul>
                {/* Submenu End */}
              </li>

              <li className="sidebar-menu__item">
                <Link to="/admin-assignments" className="sidebar-menu__link">
                  <span className="icon">
                    <i className="ph ph-file-text" />
                  </span>
                  <span className="text">Assignments</span>
                </Link>
              </li>

              <li className="sidebar-menu__item">
                <Link to="/admin-certificates" className="sidebar-menu__link">
                  <span className="icon">
                    <i className="ph ph-certificate" />
                  </span>
                  <span className="text">Certificates</span>
                </Link>
              </li>

              <li className="sidebar-menu__item">
                <Link to="/admin-certificate-manager" className="sidebar-menu__link">
                  <span className="icon">
                    <i className="ph ph-seal-check" />
                  </span>
                  <span className="text">Certificate Manager</span>
                </Link>
              </li>

              <li className="sidebar-menu__item">
                <Link to="/admin-photo-gallery" className="sidebar-menu__link">
                  <span className="icon">
                    <i className="ph ph-images" />
                  </span>
                  <span className="text">Photo Gallery</span>
                </Link>
              </li>

              <li className="sidebar-menu__item">
                <Link to="/admin-video-gallery" className="sidebar-menu__link">
                  <span className="icon">
                    <i className="ph ph-video" />
                  </span>
                  <span className="text">Video Gallery</span>
                </Link>
              </li>

              <li className={`sidebar-menu__item ${isWebsiteOpen ? 'active' : ''}`}>
                <button
                  type="button"
                  className="sidebar-menu__link"
                  onClick={toggleWebsiteDropdown}
                  aria-expanded={isWebsiteOpen}
                >
                  <span className="icon">
                    <i className="ph ph-globe" />
                  </span>
                  <span className="text">Manage Website</span>
                  <i className={`ph ph-caret-down dropdown-icon ${isWebsiteOpen ? 'rotate-180' : ''}`} />
                </button>
                {/* Submenu start */}
                <ul
                  className={`sidebar-submenu ${isWebsiteOpen ? 'open' : ''}`}
                  style={{ display: isWebsiteOpen ? 'block' : 'none' }}
                >
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-course-pages" className="sidebar-submenu__link">
                      Course Pages
                    </Link>
                  </li>
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-about-pages" className="sidebar-submenu__link">
                      Other Pages
                    </Link>
                  </li>
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-contact-page" className="sidebar-submenu__link">
                      Contact Page
                    </Link>
                  </li>
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-testimonials" className="sidebar-submenu__link">
                      Manage Testimonials
                    </Link>
                  </li>
                   <li className="sidebar-submenu__item">
                    <Link to="/admin-blogs" className="sidebar-submenu__link">
                      Manage Blogs
                    </Link>
                  </li>
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-accreditations" className="sidebar-submenu__link">
                      Manage Accreditations
                    </Link>
                  </li>
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-awards" className="sidebar-submenu__link">
                      Manage Awards
                    </Link>
                  </li>
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-infrastructure" className="sidebar-submenu__link">
                      Manage Infrastructure
                    </Link>
                  </li>
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-press-media" className="sidebar-submenu__link">
                      Manage Press & Media
                    </Link>
                  </li>
                </ul>
                {/* Submenu End */}
              </li>

              <li className={`sidebar-menu__item ${isHomepageOpen ? 'active' : ''}`}>
                <button
                  type="button"
                  className="sidebar-menu__link"
                  onClick={toggleHomepageDropdown}
                  aria-expanded={isHomepageOpen}
                >
                  <span className="icon">
                    <i className="ph ph-house" />
                  </span>
                  <span className="text">Homepage</span>
                  <i className={`ph ph-caret-down dropdown-icon ${isHomepageOpen ? 'rotate-180' : ''}`} />
                </button>
                {/* Submenu start */}
                <ul
                  className={`sidebar-submenu ${isHomepageOpen ? 'open' : ''}`}
                  style={{ display: isHomepageOpen ? 'block' : 'none' }}
                >
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-home-banners" className="sidebar-submenu__link">
                      Hero Banners
                    </Link>
                  </li>
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-home-badges" className="sidebar-submenu__link">
                      Badges Strip
                    </Link>
                  </li>
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-home-content" className="sidebar-submenu__link">
                      Homepage Content
                    </Link>
                  </li>
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-home-highlights" className="sidebar-submenu__link">
                      Key Highlights
                    </Link>
                  </li>
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-home-featured-courses" className="sidebar-submenu__link">
                      Featured Courses
                    </Link>
                  </li>
                  <li className="sidebar-submenu__item">
                    <Link to="/admin-home-counters" className="sidebar-submenu__link">
                      Counters
                    </Link>
                  </li>
                </ul>
                {/* Submenu End */}
              </li>

              <li className="sidebar-menu__item">
                <Link to="/admin-add-books" className="sidebar-menu__link">
                  <span className="icon">
                    <i className="ph ph-bookmarks" />
                  </span>
                  <span className="text">Manage Study Materials</span>
                </Link>
              </li>
              <li className="sidebar-menu__item">
                <Link to="/admin-case-studies" className="sidebar-menu__link">
                  <span className="icon">
                    <i className="ph ph-briefcase" />
                  </span>
                  <span className="text">Manage Case Studies</span>
                </Link>
              </li>
              <li className="sidebar-menu__item">
                <Link to="/admin-fees-manager" className="sidebar-menu__link">
                  <span className="icon">
                    <i className="ph ph-coins" />
                  </span>
                  <span className="text">Fees Manager</span>
                </Link>
              </li>


   <li className="sidebar-menu__item">
                <Link to="/admin-quotation" className="sidebar-menu__link">
                  <span className="icon">
                    <i className="ph ph-file-text" />
                  </span>
                  <span className="text">Generate Quotations</span>
                </Link>
              </li>

{/* <li className="sidebar-item">
  <Link to="/admin-quotation" className="sidebar-link">
    <i className="ph ph-file-text"></i>
    <span className="link-name">Fee Quotations</span>
  </Link>
</li> */}

              <li className="sidebar-menu__item">
                <span className="text-gray-300 text-sm px-20 pt-20 fw-semibold border-top border-gray-100 d-block text-uppercase">
                  Settings
                </span>
              </li>
              <li className="sidebar-menu__item">
                <Link to="/admin-profile" className="sidebar-menu__link">
                  <span className="icon">
                    <i className="ph ph-gear" />
                  </span>
                  <span className="text">Profile Settings</span>
                </Link>
              </li>
              <li className="sidebar-menu__item">
                <Link to="/admin-mail-settings" className="sidebar-menu__link">
                  <span className="icon">
                    <i className="ph ph-envelope-simple" />
                  </span>
                  <span className="text">Mail Settings</span>
                </Link>
              </li>
              <li className="sidebar-menu__item">
                <button
                  type="button"
                  className="sidebar-menu__link"
                  onClick={handleLogout}
                >
                  <span className="icon">
                    <i className="ph ph-shield-check" />
                  </span>
                  <span className="text">Logout</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </aside>
      {/* ============================ Sidebar End  ============================ */}
    </>
  );
}

export default SideBar;